import {
  CheckCircleIcon,
  EnvelopeIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { config, configUtils } from '../../config/env';

/**
 * Validation schema for the contact form
 */
const contactSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  organization: z.string().min(2, 'Organization name is required'),
  phone: z.string().optional(),
  organizationType: z.enum([
    'school',
    'npa',
    'therapy-company',
    'cooperative',
    'other',
  ]),
  message: z.string().min(10, 'Please provide more details about your needs'),
  studentCount: z.string().optional(),
  currentChallenges: z.array(z.string()).optional(),
});

/**
 * Contact form data type derived from validation schema
 */
type ContactFormData = z.infer<typeof contactSchema>;

/**
 * Form submission error interface
 */
interface SubmissionError {
  message: string;
  field?: string;
  code?: string;
}

/**
 * Form submission state enum
 */
type SubmissionState = 'idle' | 'submitting' | 'success' | 'error';

/**
 * ContactSection component for handling contact form submissions and displaying contact information.
 *
 * Features:
 * - Comprehensive form validation using Zod schema
 * - Enhanced error handling with retry mechanism
 * - Loading states and success feedback
 * - Environment-based configuration
 * - Accessibility compliant form design
 *
 * @component
 * @example
 * ```tsx
 * <ContactSection />
 * ```
 */
const ContactSection: React.FC = () => {
  const [submissionState, setSubmissionState] =
    useState<SubmissionState>('idle');
  const [submissionError, setSubmissionError] =
    useState<SubmissionError | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
  });

  const onSubmit = async (data: ContactFormData) => {
    setSubmissionState('submitting');
    setSubmissionError(null);

    try {
      const submissionUrl = configUtils.getContactFormSubmissionUrl();
      configUtils.debugLog('Submitting contact form:', data);
      configUtils.debugLog('Using submission URL:', submissionUrl);

      const response = await fetch(submissionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.message ||
            `HTTP ${response.status}: ${response.statusText}`
        );
      }

      const result = await response.json();
      configUtils.debugLog('Contact form submitted successfully:', result);

      setSubmissionState('success');
      reset();
      setRetryCount(0);
    } catch (error) {
      configUtils.debugLog('Contact form submission error:', error);

      const errorMessage =
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred while submitting the form';

      // Check for specific error types
      if (error instanceof TypeError && error.message.includes('fetch')) {
        setSubmissionError({
          message:
            'Unable to connect to the server. Please check your internet connection and try again.',
          code: 'NETWORK_ERROR',
        });
      } else if (errorMessage.includes('400')) {
        setSubmissionError({
          message: 'Please check your information and try again.',
          code: 'VALIDATION_ERROR',
        });
      } else if (errorMessage.includes('429')) {
        setSubmissionError({
          message:
            'Too many requests. Please wait a moment before trying again.',
          code: 'RATE_LIMIT_ERROR',
        });
      } else if (errorMessage.includes('500')) {
        setSubmissionError({
          message:
            'Server error. Our team has been notified. Please try again later.',
          code: 'SERVER_ERROR',
        });
      } else {
        setSubmissionError({
          message: errorMessage,
          code: 'UNKNOWN_ERROR',
        });
      }

      setSubmissionState('error');
      setRetryCount(prev => prev + 1);
    }
  };

  const resetForm = () => {
    setSubmissionState('idle');
    setSubmissionError(null);
    setRetryCount(0);
  };

  // Success state
  if (submissionState === 'success') {
    return (
      <section id="contact" className="section-padding bg-white">
        <div className="container-padding">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircleIcon className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-secondary-900 mb-4">
              Thank You for Your Interest!
            </h2>
            <p className="text-xl text-secondary-600 mb-8">
              We've received your message and will get back to you within 24
              hours. Our team is excited to show you how TherapyLink can
              transform your organization.
            </p>
            <button onClick={resetForm} className="btn-secondary">
              Send Another Message
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="section-padding bg-white" aria-label="Contact form section">
      <div className="container-padding">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-secondary-900 mb-4">
            Get Started with TherapyLink
          </h2>
          <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
            Ready to transform your therapy service operations? Contact us for a
            personalized demo and see how TherapyLink can benefit your
            organization.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div>
            <h3 className="text-2xl font-bold text-secondary-900 mb-6">
              Let's Connect
            </h3>

            <div className="space-y-6">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mr-4">
                  <EnvelopeIcon className="w-6 h-6 text-primary-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-secondary-900 mb-1">
                    Email Us
                  </h4>
                  <p className="text-secondary-600">
                    <a
                      href={`mailto:${config.contactEmail}`}
                      className="hover:text-primary-600 transition-colors duration-200"
                    >
                      {config.contactEmail}
                    </a>
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mr-4">
                  <PhoneIcon className="w-6 h-6 text-primary-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-secondary-900 mb-1">
                    Call Us
                  </h4>
                  <p className="text-secondary-600">
                    <a
                      href={`tel:${config.salesPhone.replace(/\s/g, '')}`}
                      className="hover:text-primary-600 transition-colors duration-200"
                    >
                      {config.salesPhone}
                    </a>
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 p-6 bg-primary-50 rounded-lg">
              <h4 className="font-semibold text-secondary-900 mb-3">
                What to Expect:
              </h4>
              <ul className="space-y-2 text-secondary-600">
                <li className="flex items-center">
                  <CheckCircleIcon className="w-4 h-4 text-primary-600 mr-2" />
                  Personalized demo tailored to your needs
                </li>
                <li className="flex items-center">
                  <CheckCircleIcon className="w-4 h-4 text-primary-600 mr-2" />
                  Implementation timeline discussion
                </li>
                <li className="flex items-center">
                  <CheckCircleIcon className="w-4 h-4 text-primary-600 mr-2" />
                  Custom pricing based on your organization
                </li>
                <li className="flex items-center">
                  <CheckCircleIcon className="w-4 h-4 text-primary-600 mr-2" />
                  Support and training information
                </li>
              </ul>
            </div>
          </div>

          {/* Contact Form */}
          <div className="card">
            {/* Error Display */}
            {submissionState === 'error' && submissionError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg
                      className="w-5 h-5 text-red-400 mt-0.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      Submission Failed
                    </h3>
                    <p className="text-sm text-red-700 mt-1">
                      {submissionError.message}
                    </p>
                    {retryCount >= 3 && (
                      <p className="text-sm text-red-600 mt-2">
                        If the problem persists, please contact us directly at{' '}
                        <a
                          href={`mailto:${config.contactEmail}`}
                          className="underline hover:text-red-800"
                        >
                          {config.contactEmail}
                        </a>
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" aria-label="Contact form" noValidate>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-secondary-700 mb-2"
                  >
                    Full Name *
                  </label>
                  <input
                    {...register('name')}
                    type="text"
                    id="name"
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                    placeholder="Your full name"
                    aria-required="true"
                    aria-invalid={errors.name ? 'true' : 'false'}
                    aria-describedby={errors.name ? 'name-error' : undefined}
                  />
                  {errors.name && (
                    <p id="name-error" className="text-red-600 text-sm mt-1" role="alert">
                      {errors.name.message}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-secondary-700 mb-2"
                  >
                    Email Address *
                  </label>
                  <input
                    {...register('email')}
                    type="email"
                    id="email"
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                    placeholder="your.email@organization.edu"
                    aria-required="true"
                    aria-invalid={errors.email ? 'true' : 'false'}
                    aria-describedby={errors.email ? 'email-error' : undefined}
                  />
                  {errors.email && (
                    <p id="email-error" className="text-red-600 text-sm mt-1" role="alert">
                      {errors.email.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="organization"
                    className="block text-sm font-medium text-secondary-700 mb-2"
                  >
                    Organization Name *
                  </label>
                  <input
                    {...register('organization')}
                    type="text"
                    id="organization"
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                    placeholder="Organization Name"
                  />
                  {errors.organization && (
                    <p className="text-red-600 text-sm mt-1">
                      {errors.organization.message}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-secondary-700 mb-2"
                  >
                    Phone Number
                  </label>
                  <input
                    {...register('phone')}
                    type="tel"
                    id="phone"
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="organizationType"
                    className="block text-sm font-medium text-secondary-700 mb-2"
                  >
                    Organization Type *
                  </label>
                  <select
                    {...register('organizationType')}
                    id="organizationType"
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                  >
                    <option value="">Select type...</option>
                    <option value="school">School</option>
                    <option value="npa">NPA</option>
                    <option value="therapy-company">Therapy Company</option>                    
                    <option value="cooperative">Education Cooperative</option>                    
                    <option value="other">Other</option>
                  </select>
                  {errors.organizationType && (
                    <p className="text-red-600 text-sm mt-1">
                      {errors.organizationType.message}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="studentCount"
                    className="block text-sm font-medium text-secondary-700 mb-2"
                  >
                    Approximate Student Count
                  </label>
                  <select
                    {...register('studentCount')}
                    id="studentCount"
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                  >
                    <option value="">Select range...</option>
                    <option value="0-500">0-500 students</option>
                    <option value="500-2000">500-2,000 students</option>
                    <option value="2000-10000">2,000-10,000 students</option>
                    <option value="10000+">10,000+ students</option>
                  </select>
                </div>
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-secondary-700 mb-2"
                >
                  Message *
                </label>
                <textarea
                  {...register('message')}
                  id="message"
                  rows={4}
                  className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                  placeholder="Tell us about your current challenges and what you'd like to achieve..."
                  aria-required="true"
                  aria-invalid={errors.message ? 'true' : 'false'}
                  aria-describedby={errors.message ? 'message-error' : undefined}
                />
                {errors.message && (
                  <p id="message-error" className="text-red-600 text-sm mt-1" role="alert">
                    {errors.message.message}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={submissionState === 'submitting'}
                className="w-full btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                aria-busy={submissionState === 'submitting'}
                aria-label={submissionState === 'submitting' ? 'Submitting form...' : 'Submit contact form'}
              >
                {submissionState === 'submitting' ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Sending...
                  </>
                ) : submissionState === 'error' && retryCount > 0 ? (
                  `Try Again (${retryCount + 1})`
                ) : (
                  'Request Demo'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
